import { Icon } from '@dynamic/ui';
import { useEffect } from 'react';
import styled from 'styled-components';

import { hoverOpacity } from '#/components/styles';
import { useResizeObserver } from '#/hooks/useResizeObserver';
import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { falsyToNull, remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

interface BannerProps {
  /** Is this an error style notification? */
  error: boolean;
  /** Text displayed in the banner, or its translation key */
  message: string;
  /** A callback function to be called when the banner is dismissed */
  onDismiss?: () => void;
  /** Aria label for the dismiss button, or its translation key */
  dismissAriaLabel?: string;
}

interface BannerDivProps {
  /** Is this an error style notification? */
  $error: boolean;
  /** Make space for the dismiss button? */
  $hasDismiss: boolean;
}

const BannerContainer = styled.div<BannerDivProps>`
  gap: ${remify(50)};
  padding: ${remify(24)} ${remify(50)};

  @media screen and ${MEDIA_QUERIES.xs} {
    gap: ${remify(16)};
    padding: ${remify(16)};
  }

  display: grid;
  align-items: center;
  grid-template-columns: ${({ $hasDismiss }) =>
    $hasDismiss ? '1fr min-content' : '1fr'};
  background-color: ${({ $error }) =>
    $error
      ? `var(--bannerExceptionBackground)`
      : `var(--bannerInfoBackground)`};
  color: ${({ $error }) =>
    $error
      ? `var(--bannerExceptionForeground)`
      : `var(--bannerInfoForeground)`};
  font: var(--fontStyleCallout);
  text-align: center;
  position: relative;
  z-index: 1;
`;

const CloseButton = styled.button`
  color: var(--pageParagraphForeground);
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-size: 0;

  ${hoverOpacity}
`;

const CloseIcon = styled(Icon)`
  width: ${remify(24)};
  height: ${remify(24)};
`;

const setBannerHeight = (height: number) => {
  // put the banner height in a css variable, so all the components can react accordingly.
  if (height) {
    document.body.style.setProperty('--bannerHeight', remify(height));
  } else {
    document.body.style.removeProperty('--bannerHeight');
  }
};

export const Banner = ({
  error,
  message: messageKey,
  onDismiss,
  dismissAriaLabel: dismissAriaLabelKey,
}: BannerProps) => {
  const { ref } = useResizeObserver<HTMLDivElement>((target) => {
    setBannerHeight(target.clientHeight);
  });

  // reset banner height on unmount
  useEffect(() => {
    return () => setBannerHeight(0);
  }, []);

  const hasDismiss = Boolean(onDismiss);

  const message = tb(messageKey);
  const dismissAriaLabel = tb(
    falsyToNull(dismissAriaLabelKey) ?? 'button.dismiss'
  );

  return (
    <BannerContainer
      $error={error}
      $hasDismiss={hasDismiss}
      ref={ref}
      role="alert"
    >
      {message}
      {hasDismiss && (
        <CloseButton
          aria-label={dismissAriaLabel}
          onClick={onDismiss}
          title={dismissAriaLabel}
        >
          <CloseIcon aria-hidden="true" name="cross" />
        </CloseButton>
      )}
    </BannerContainer>
  );
};
